import React, { useState } from 'react';
import EditNoteIcon from '@mui/icons-material/EditNote';

import EditModal from '../modals/EditModal';
import InputField from '../../common/fields/InputField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import AppSnackbar from '../../common/popups/AppSnackbar';
import useProfileSection from '../../../hooks/useProfileSection';

function AboutSection({ teacher, canEdit, onUpdate }) {
  const { isEditing, openModal, closeModal, save, errors, loading, snackbarProps } =
    useProfileSection(teacher.profileId, onUpdate);

  const [modalData, setModalData] = useState({
    bio: teacher.bio,
    introVideoUrl: teacher.videoUrl || '',
  });

  const getEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      let videoId = '';
      if (urlObj.hostname.includes('youtube.com')) videoId = urlObj.searchParams.get('v');
      if (urlObj.hostname === 'youtu.be') videoId = urlObj.pathname.slice(1);
      if (!videoId) return '';
      return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return '';
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    save(modalData, { successMessage: 'About section updated!' });
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <h2 className="uppercase text-2xl flex items-center">
          <span className="bg-gradient-primary w-2 h-8 mr-3 rounded-full"></span>
          About Me
        </h2>
        {canEdit && (
          <button onClick={openModal} className="text-primary hover:text-orange-400">
            <EditNoteIcon fontSize="large" />
          </button>
        )}
      </div>

      <p className="text-gray-400 leading-relaxed break-words">{teacher.bio}</p>

      <hr className="mt-5 mb-8 border-white/20 w-full" />

      {teacher.videoUrl ? (
        <div className="relative w-full pb-[56.25%] h-0 overflow-hidden">
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={getEmbedUrl(teacher.videoUrl)}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="text-center py-10 bg-gradient-glass rounded-lg">
          <p className="text-gray-400 text-lg">No video added yet.</p>
        </div>
      )}

      <EditModal isOpen={isEditing} onClose={closeModal} title="Edit About Me">
        <form onSubmit={handleSave}>
          <div className="space-y-4 mb-10">
            <InputField
              label="About you"
              value={modalData.bio}
              multiline
              rows={6}
              onChange={(e) => setModalData({ ...modalData, bio: e.target.value })}
              placeholder="Write about yourself..."
              error={!!errors.bio}
              helperText={errors.bio}
            />
            <InputField
              label="Intro video URL"
              type="url"
              value={modalData.introVideoUrl}
              onChange={(e) => setModalData({ ...modalData, introVideoUrl: e.target.value })}
              placeholder="Enter YouTube video URL"
              error={!!errors.introVideoUrl}
              helperText={errors.introVideoUrl}
            />
          </div>
          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </PrimaryButton>
          </div>
        </form>
      </EditModal>

      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default AboutSection;
